import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { AppConfigUpdateModel, IndexPageBase, WebsiteImage } from '@flipdish/api-client-typescript';
import { Hidden, makeStyles, Paper, Switch, Theme, Typography } from '@material-ui/core';
import { Field, FieldProps, FormikProps, withFormik } from 'formik';
import { debounce, isEqual, memoize } from 'lodash';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import useIsMounted from '../../../custom-hooks/useIsMounted';
import useRequestSnackBar, {
  useRequestSnackBarIndicators,
} from '../../../custom-hooks/useRequestSnackBar';
import { myFlipdish } from '../../../helpers/externalLinks';
import { getSelectedApp } from '../../../selectors/app.selector';
import {
  createLoadingErrorSelectorFactory,
  createLoadingSelector,
} from '../../../selectors/loading.selector';
import {
  ADD_WEBSITE_SETTINGS_TESTIMONIAL,
  DELETE_WEBSITE_SETTINGS_IMAGE,
  DELETE_WEBSITE_SETTINGS_TESTIMONIAL,
  GET_WEBSITE_SETTINGS,
  getWebsiteSettings,
  SET_APP_CONFIG,
  SET_APP_CONFIG_LANGUAGES,
  SET_WEBSITE_SETTINGS,
  setAppConfig,
  setWebsiteSettings,
  UPDATE_WEBSITE_SETTINGS_TESTIMONIAL,
  UPLOAD_APP_CONFIG_LOGO,
  UPLOAD_WEBSITE_SETTINGS_IMAGE,
  uploadWebsiteSettingsImage,
} from '../actions';
import LayoutWrapper, { LayoutContext } from '../components/LayoutWrapper';
import WebsitePreview from '../components/WebsitePreview';
import { ImageLocationEnum } from '../service';
import EditWebsiteSettingsAboutSectionsPanel from './EditWebsiteSettingsAboutSectionsPanel';
import EditWebsiteSettingsContactFormPanel from './EditWebsiteSettingsContactFormPanel';
import EditWebsiteSettingsImagesPanel from './EditWebsiteSettingsImagesPanel';
import EditWebsiteSettingSkeleton from './EditWebsiteSettingSkeleton';
import EditWebsiteSettingsOpeningHoursPanel from './EditWebsiteSettingsOpeningHoursPanel';
import { Testimonial } from './EditWebsiteSettingsTestimonialsItem';
import EditWebsiteSettingsTestimonialsPanel from './EditWebsiteSettingsTestimonialsPanel';

const loadingSelector = createLoadingSelector([GET_WEBSITE_SETTINGS]);
const savingSelector = createLoadingSelector([
  SET_APP_CONFIG,
  SET_APP_CONFIG_LANGUAGES,
  UPLOAD_APP_CONFIG_LOGO,
  SET_WEBSITE_SETTINGS,
  UPDATE_WEBSITE_SETTINGS_TESTIMONIAL,
  ADD_WEBSITE_SETTINGS_TESTIMONIAL,
  DELETE_WEBSITE_SETTINGS_TESTIMONIAL,
  DELETE_WEBSITE_SETTINGS_IMAGE,
  UPLOAD_WEBSITE_SETTINGS_IMAGE,
]);
const errorSelector = createLoadingErrorSelectorFactory([
  SET_APP_CONFIG,
  SET_APP_CONFIG_LANGUAGES,
  UPLOAD_APP_CONFIG_LOGO,
]);

export const processImages = memoize((Images: WebsiteImage[]) => {
  return Images.reduce<{ [key: string]: any }>(
    (res, image) => {
      if (image.ImageLocation) {
        const currentImages = res[image.ImageLocation];
        if (currentImages && Array.isArray(currentImages)) {
          currentImages.push(image);
        } else {
          res[image.ImageLocation] = image;
        }
      }

      return res;
    },
    {
      [ImageLocationEnum.IndexHeader]: [],
      [ImageLocationEnum.IndexGallery]: [],
    } as any
  );
});

const useStyles = makeStyles(({ breakpoints }: Theme) => ({
  wrapper: {
    width: '32%',
    maxWidth: 360,
    height: '100%',
    overflow: 'hidden',
    [breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: 'none',
    },
  },
  content: {
    width: 'calc(100% + 20px)',
    height: '100%',
    overflowY: 'scroll',
  },
  toggleHeader: {
    boxShadow: 'none',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `12px 20px`,

    '&:last-child': {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },

    '&::before': {
      top: '-1px',
      left: 0,
      right: 0,
      height: '1px',
      content: '""',
      opacity: 1,
      position: 'absolute',
      transition:
        'opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      backgroundColor: 'rgba(0, 0, 0, 0.20)',
    },
  },
  panels: {
    position: 'relative',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    background: 'rgba(255, 255, 255, 0.3)',
  },
  panel: {
    boxShadow: 'none',
    '&::before': {
      // @ts-ignore
      opacity: '1 !important' as number,
    },
    '& .MuiExpansionPanelSummary-root': {
      paddingLeft: 20,
      paddingRight: 20,
    },
  },
  firstPanel: {
    '&::before': {
      display: 'none',
    },
  },
  statusBar: (props: Props) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 20px',
    backgroundColor: props.currentApp.IsPanaceaEnabled
      ? 'rgba(54, 216, 142, 0.2)'
      : 'rgba(0, 0, 0, 0.1)',
  }),
  statusText: {
    fontSize: 16,
    lineHeight: 1.5,
    letterSpacing: 0.15,
    color: 'rgba(0, 0, 0, 0.87)',
  },
}));

type FormValues = ReturnType<typeof mapPropsToValues>;

const mapPropsToValues = (props: Props) => {
  const { settings } = props;

  return settings;
};

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const mapStateToProps = (state: AppState) => {
  const {
    settings: { Images = [], Testimonials = [], ...settings },
  } = state.website;

  return {
    testimonials: Testimonials as Testimonial[],
    images: processImages(Images),
    settings,
    translate: getTranslate(state.locale),
    appId: getSelectedApp(state).AppId,
    loading: loadingSelector(state),
    saving: savingSelector(state),
    currentApp: getSelectedApp(state),
    error: errorSelector(state),
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  setSettings: (settings: IndexPageBase) => dispatch(setWebsiteSettings(settings)),
  getSettings: () => dispatch(getWebsiteSettings()),
  uploadImage: (imageLocation: WebsiteImage.ImageLocationEnum, file) =>
    dispatch(uploadWebsiteSettingsImage(imageLocation, file)),
  updateConfig: (config: AppConfigUpdateModel) => dispatch(setAppConfig(config)),
});

export default compose<Props, {}>(
  connect(mapStateToProps, mapDispatchToProps),
  withFormik<Props, FormValues>({
    displayName: 'EditWebsiteSettings',
    enableReinitialize: true,
    mapPropsToValues,
    handleSubmit: (values: FormValues, formikBag) => {
      const {
        props: { settings, setSettings },
      } = formikBag;

      if (!isEqual(values, settings)) {
        setSettings(values);
      }
    },
  })
)((props: Props & FormikProps<FormValues>) => {
  const {
    translate,
    appId,
    images,
    settings,
    getSettings,
    testimonials,
    submitForm,
    values,
    dirty,
    loading,
    saving,
    error,
    currentApp,
    updateConfig,
  } = props;
  const [isPanaceaEnabled, setIsPanaceaEnabled] = useState(currentApp.IsPanaceaEnabled);
  const isMounted = useIsMounted();
  const [timestamp, setTimestamp] = useState(Date.now());
  const [selectedSection, setSelectedSection] = useState<string | undefined>();
  const [openedSection, setOpenedSection] = useState<string | undefined>();

  const classes = useStyles(props);

  const toggleSubmit = useCallback(
    (value: boolean) => updateConfig({ ...currentApp, IsPanaceaEnabled: value }),
    []
  );

  const url = useMemo(
    () =>
      `${myFlipdish(String(currentApp.panaceaVanityUrl))}?touch=${timestamp}/#${selectedSection}`,
    [timestamp, selectedSection, isPanaceaEnabled]
  );

  const handleSectionOpening = useCallback((name?: string) => {
    setSelectedSection(name);
    setOpenedSection(name);
  }, []);
  const handleEnabling = useCallback((name?: string) => {
    setSelectedSection(name);
  }, []);
  const submit = useMemo(
    () =>
      debounce(() => {
        if (isMounted()) {
          submitForm();
        }
      }, 3000),
    []
  );
  const updateTimestamp = useMemo(
    () =>
      debounce(() => {
        if (isMounted()) {
          setTimestamp(Date.now());
        }
      }, 1000),
    []
  );

  useEffect(() => {
    updateTimestamp();
  }, [settings]);

  // @ts-ignore
  const { success, failure } = useRequestSnackBarIndicators(saving, error);

  useEffect(() => {
    getSettings();
  }, []);

  useEffect(() => {
    if (dirty && values) {
      submit();
    }
  }, [dirty, values]);

  useRequestSnackBar({
    loading,
    success,
    failure,
    failureMessage: String(error),
    autoSaving: saving,
  });

  return (
    <LayoutWrapper
      title="Live_website_editor"
      toParent={`/${currentApp.AppId}/sales-channels/website-settings`}
      fullWidth
      strictToParent
      documentTitle="Web_editor"
    >
      <div className={classes.wrapper}>
        <div className={classes.content}>
          {loading ? (
            <EditWebsiteSettingSkeleton />
          ) : (
            <>
              <div className={classes.statusBar}>
                <Typography variant="caption" className={classes.statusText}>
                  {translate(
                    currentApp.IsPanaceaEnabled
                      ? 'Disable_website_caption'
                      : 'Enable_website_caption'
                  )}
                </Typography>
                <Switch
                  color="default"
                  inputProps={{ 'data-fd': 'toggle-website' }}
                  checked={isPanaceaEnabled}
                  onChange={(e, value: boolean) => {
                    setIsPanaceaEnabled(value);
                    toggleSubmit(value);
                  }}
                />
              </div>
              <div className={classes.panels}>
                {!currentApp.IsPanaceaEnabled ? <div className={classes.overlay} /> : null}
                <EditWebsiteSettingsImagesPanel
                  name=""
                  expanded={'' === openedSection}
                  onExpand={handleSectionOpening}
                  onToggle={handleEnabling}
                  className={classes.panel}
                  images={images[ImageLocationEnum.IndexHeader]}
                  imageLocation={WebsiteImage.ImageLocationEnum.IndexHeader}
                  fdKeyName="carousel"
                  title="Carousel"
                  maxImages={6}
                  minImages={1}
                />
                <EditWebsiteSettingsAboutSectionsPanel
                  name="aboutSection"
                  onExpand={handleSectionOpening}
                  onToggle={handleEnabling}
                  expanded={'aboutSection' === openedSection}
                  settings={values}
                  className={classes.panel}
                  AboutSectionLeftImage={images[ImageLocationEnum.IndexAboutSectionLeft]}
                  AboutSectionRightImage={images[ImageLocationEnum.IndexAboutSectionRight]}
                />
                <EditWebsiteSettingsOpeningHoursPanel
                  name="timetable"
                  initialImage={images[ImageLocationEnum.IndexOpeningHoursHeader]}
                  onExpand={handleSectionOpening}
                  onToggle={handleEnabling}
                  className={classes.panel}
                  expanded={'timetable' === openedSection}
                />
                <Paper className={classes.toggleHeader} square>
                  <Translate id="Menu_preview" />
                  <Field name="MenuPreviewEnabled">
                    {(fPRops: FieldProps) => {
                      const { field, form } = fPRops;

                      return (
                        <Switch
                          value=""
                          color="default"
                          data-fd="website_settings_menu_preview_toggle"
                          checked={field.value}
                          onChange={(e, value: boolean) => {
                            form.setFieldValue(field.name, value);
                            setSelectedSection('menu');
                          }}
                        />
                      );
                    }}
                  </Field>
                </Paper>
                <EditWebsiteSettingsImagesPanel
                  name="gallery"
                  expanded={'gallery' === openedSection}
                  onExpand={handleSectionOpening}
                  onToggle={handleEnabling}
                  className={classes.panel}
                  fdKeyName="gallery"
                  title="Gallery"
                  maxImages={9}
                  minImages={2}
                  toggleFieldName="GalleryEnabled"
                  images={images[ImageLocationEnum.IndexGallery]}
                  imageLocation={WebsiteImage.ImageLocationEnum.IndexGallery}
                  toggleable
                />
                <EditWebsiteSettingsTestimonialsPanel
                  name="testimonials"
                  data={testimonials}
                  className={classes.panel}
                  image={images[ImageLocationEnum.IndexTestimonialsHeader]}
                  expanded={'testimonials' === openedSection}
                  onExpand={handleSectionOpening}
                  onToggle={handleEnabling}
                />
                <EditWebsiteSettingsContactFormPanel
                  name="contact"
                  settings={values}
                  className={classes.panel}
                  expanded={'contact' === openedSection}
                  onExpand={handleSectionOpening}
                  onToggle={handleEnabling}
                />
                <Paper className={classes.toggleHeader}>
                  <Translate id="Map" />
                  <Field name="MapEnabled">
                    {(fPRops: FieldProps) => {
                      const { field, form } = fPRops;

                      return (
                        <Switch
                          value=""
                          color="default"
                          data-fd="website_settings_map_toggle"
                          checked={field.value}
                          onChange={(e, value: boolean) => {
                            form.setFieldValue(field.name, value);
                            setSelectedSection('maps');
                          }}
                        />
                      );
                    }}
                  </Field>
                </Paper>
              </div>
            </>
          )}
        </div>
      </div>
      <Hidden smDown>
        <LayoutContext.Consumer>
          {(view: any) => <WebsitePreview view={view} url={url} />}
        </LayoutContext.Consumer>
      </Hidden>
    </LayoutWrapper>
  );
});
